import React, { useEffect, useState } from 'react';
import { Drawer } from 'antd';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';
import { DateTimePickerInput } from 'v3/Components/Form/DateTimePickers/DateTimePicker';
import TextArea from 'v3/Components/Form/TextArea';
import { PrimaryButton, TertiaryButton } from 'v3/Components/Shared/Buttons';
import { useToasts } from 'Context';
import { buildEndpoint, useDataPoster } from 'Utilities';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';

type ReviewerProfileEditFollowUpDrawerProps = {
  reviewerId: string;
  followupDate?: string;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

const ReviewerProfileEditFollowUpDrawer: React.FC<ReviewerProfileEditFollowUpDrawerProps> = ({
  reviewerId,
  followupDate,
  open,
  onClose,
  onSuccess,
}) => {
  const toast = useToasts();
  const [date, setDate] = useState('');
  const [dateError, setDateError] = useState<string | undefined>();
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  // Pre-populate with the reviewer's current follow-up date whenever the drawer opens.
  useEffect(() => {
    if (!open) return;
    setDate(followupDate ?? '');
    setDateError(undefined);
    setNote('');
  }, [open, followupDate]);

  const [postData] = useDataPoster(
    buildEndpoint(ENDPOINTS.reviewers.base, reviewerId, 'followupDate'),
    undefined,
    true,
    '3.0'
  );

  const handleSave = async () => {
    setSaving(true);
    setDateError(undefined);
    try {
      const resp: any = await postData({ followupDate: date, note });
      if (resp?.status === 200) {
        toast.success('Changes saved successfully');
        onSuccess();
        onClose();
      } else if (resp?.response?.status === 400) {
        const data = resp.response?.data;
        if (data?.errors) {
          const mappedErrors: Record<string, string> = {};
          Object.entries(data.errors).forEach(([key, val]) => {
            const field = key.charAt(0).toLowerCase() + key.slice(1);
            mappedErrors[field] = (val as string[])[0];
          });
          setDateError(mappedErrors.followupDate);
          toast.error('Please validate the highlighted fields');
        } else {
          toast.error(data?.message ?? 'Failed to save changes. Please try again.');
        }
      } else if (resp?.response?.status === 403) {
        toast.error(resp.response?.data?.message ?? 'You do not have permission to perform this action.');
      } else if ((resp?.response?.status ?? 0) >= 500) {
        toast.error('Failed to save changes. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <div className="sl-drawer-footer">
      <TertiaryButton title="Cancel" onClick={onClose} />
      <PrimaryButton title="Save" onClick={handleSave} disabled={saving}>
        {saving && <CircularProgress size={16} thickness={5} className="sl-drawer-save-spinner" />}
      </PrimaryButton>
    </div>
  );

  return (
    <Drawer
      visible={open}
      onClose={onClose}
      title={<span className="sl-drawer-title">Edit Follow Up</span>}
      width={480}
      closable
      closeIcon={<CloseIcon />}
      footer={footer}
      destroyOnClose
      className="sl-drawer"
    >
      <div className="sl-drawer-fields-section">
        <DateTimePickerInput
          label="Follow-up Date"
          required
          dateOnly
          isTable
          minDate={new Date()}
          value={date}
          onChange={(val: string) => {
            setDate(val);
            setDateError(undefined);
          }}
          error={dateError}
        />
        <TextArea
          label="Notes"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={5}
        />
      </div>
    </Drawer>
  );
};

export default ReviewerProfileEditFollowUpDrawer;
